import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  updateInputIntervals,
  updateRealtyTransactions,
  updateRealtyTransactionsFiltersAdd,
  clearFiltersTransactions,
} from './redux/realtyTransactions.actions';

import {
  handleInputsIntervals,
  handlePriceInputs,
  validateForm,
} from './redux/realtyTransactions.controller';

import { updateClusters, updateMarkers } from '../redux/dashboardActions';
import { TransactionsGeneralInputs } from '../types';

import {
  Input,
  IResponse,
  Select,
  SelectCalendar,
  MenuWrapper,
  Spinner,
} from '../../../component';

import './realtyTransactions.scss';
import {
  setMaxDate,
  setMaxUsefulArea,
  setMinDate,
  setMinMaxAreaLocalFilters,
  setMinMaxDateLocalFilters,
  setMinUsefulArea,
} from '../charts/redux/charts.actions';
import { getAvaliableFilters } from './redux/realtyTransactions.infra';
import { AxiosResponse } from 'axios';

type RealtyTransactionsProps = {
  closeModalAction: () => void;
};

const RealtyTransactionsGeneral = ({
  closeModalAction,
}: RealtyTransactionsProps) => {
  const dispatch = useDispatch();

  const { preference } = useSelector((state: any) => state.layoutState);

  const { realtyTransactions, realtyTransactionsFiltersOptions } = useSelector(
    (state: any) => state.realtyTransactionsGeneralState
  );

  const { minMaxArea, minMaxDate } = useSelector(
    (state: any) => state.chartsState.localFilters
  );

  const { inputIntervals } = realtyTransactions;
  const { timeframe } = inputIntervals;
  const { realtyTypes } = realtyTransactionsFiltersOptions;

  const nls = require(`./nls/${preference.language.id}.json`);

  const { markers, clusters } = useSelector(
    (state: any) => state.dashboardState
  );

  const { elastic_city_id } = useSelector(
    (state: any) => state.mapSearch.selectedCity || {}
  );

  const [validation, setValidation]: any = useState({});
  const [realtyTypeFilterIsActive, setRealtyTypeFilterIsActive] =
    useState(false);
  const [filtersErrorMessage, setFiltersErrorMessage] = useState<null | string>(
    null
  );
  const [avaliableInputIntervals, setAvaliableInputIntervals] =
    useState(inputIntervals);
  const [filtersInRegion, setFiltersInRegion] = useState<null>(null);
  const [avaliableFilterYears, setAvaliableFilterYears] = useState([
    '2020',
    '2021',
    '2022',
    '2023',
  ]);
  const [avaliableProgramFilters, setAvaliableProgramFilters] = useState({
    bedroom: true,
    suite: true,
    garage: true,
  });

  const clearAvaliableFiltersState = () => {
    setAvaliableInputIntervals(false);
    setFiltersErrorMessage(null);
    setRealtyTypeFilterIsActive(false);
    setFiltersInRegion(null);
  };

  const updateRealtyProgramFilters = (
    filterName: string,
    filterValue: boolean
  ) => {
    const updatedFilters = { ...avaliableProgramFilters };

    if (filterName === 'bedroom') {
      updatedFilters.bedroom = filterValue;
    } else if (filterName === 'garage') {
      updatedFilters.garage = filterValue;
    } else if (filterName === 'suite') {
      updatedFilters.suite = filterValue;
    }

    setAvaliableProgramFilters(updatedFilters);
  };

  const avaliableFiltersRules = (
    filtersData: Record<string, boolean> | null
  ) => {
    let inputIntervalsList = inputIntervals;
    let realtyProgramFilters = avaliableProgramFilters;

    if (filtersData) {
      Object.entries(filtersData.filters)?.map((filter) => {
        const filterName: string = filter[0];
        const filterValue: boolean = filter[1];

        if (filterName === 'realtyType') {
          setRealtyTypeFilterIsActive(filterValue);
        }

        if (!filterValue) {
          switch (filterName) {
            case 'advertisementPeriod': {
              delete inputIntervalsList['timeframe'];
              break;
            }
            case 'squareMeterPrice': {
              delete inputIntervalsList['pricePerSquareMt'];
              break;
            }
            case 'realtyAge': {
              delete inputIntervalsList['realtyAge'];
              break;
            }
            case 'pricePerSquareMt': {
              delete inputIntervalsList['pricePerSquareMt'];
              break;
            }
            case 'bedroom':
            case 'garage':
            case 'suite':
              updateRealtyProgramFilters(filterName, filterValue);
              break;
            default: {
              break;
            }
          }
        }

        setAvaliableInputIntervals(inputIntervalsList);
        setAvaliableProgramFilters(realtyProgramFilters);
      });
    }
  };

  const getAvaliableFiltersByCity = (cityId: string, isUpdate?: boolean) => {
    if (!isUpdate) {
      clearAvaliableFiltersState();

      Promise.resolve(getAvaliableFilters(cityId))
        .then((response: AxiosResponse) => {
          const { data } = response;

          if (data) {
            avaliableFiltersRules(data);
            setFiltersInRegion(data);
          }
        })
        .catch((error) => {
          console.error(error);
          setFiltersErrorMessage(`${nls.error.avaliableFiltersError}`);
        });
    } else {
      avaliableFiltersRules(filtersInRegion);
    }
  };

  useEffect(() => {
    getAvaliableFiltersByCity(elastic_city_id);
  }, [elastic_city_id]);

  useEffect(() => {
    getAvaliableFiltersByCity(elastic_city_id, true);
  }, [timeframe]);

  const inputPatterns: TransactionsGeneralInputs = {
    timeframemin: {
      pattern: 'number',
      erro: validation,
      message:
        validation.timeframemin === 'biggerThanMax' ? nls.minBiggerThanMax : '',
    },
    timeframemax: {
      pattern: 'number',
      erro: validation,
      message:
        validation.timeframemax === 'smallerThanMin'
          ? nls.maxSmallerThanMin
          : '',
    },
    privateAreaIntervalmin: {
      pattern: 'number',
      erro: validation,
      message:
        validation.privateAreaIntervalmin === 'biggerThanMax'
          ? nls.minBiggerThanMax
          : '',
    },
    privateAreaIntervalmax: {
      pattern: 'number',
      erro: validation,
      message:
        validation.privateAreaIntervalmax === 'smallerThanMin'
          ? nls.maxSmallerThanMin
          : '',
    },
    realtyAgemin: {
      pattern: 'number',
      erro: validation,
      message:
        validation.realtyAgemin === 'biggerThanMax' ? nls.minBiggerThanMax : '',
    },
    realtyAgemax: {
      pattern: 'number',
      erro: validation,
      message:
        validation.realtyAgemax === 'smallerThanMin'
          ? nls.maxSmallerThanMin
          : '',
    },
  };

  const handleInput = (
    e: any,
    v: IResponse,
    tuple: { min: string; max: string }
  ) => {
    let newMinValue: string | number = 0;
    let newMaxValue: string | number = 0;
    if (v.name.includes('privateAreaIntervalmin')) {
      newMinValue = e.value.length > 0 ? e.value : 0;
      dispatch(setMinMaxAreaLocalFilters({ ...minMaxArea, min: newMinValue }));
    } else if (v.name.includes('privateAreaIntervalmax')) {
      newMaxValue = e.value.length > 0 ? e.value : 0;
      dispatch(setMinMaxAreaLocalFilters({ ...minMaxArea, max: newMaxValue }));
    }
    const typeNumber = e.name.includes('timeframe') ? 'int' : '';
    if (e.name.toLowerCase().includes('price')) {
      dispatch(handlePriceInputs(e, v, tuple, typeNumber));
    } else {
      dispatch(handleInputsIntervals(e, v, tuple, typeNumber));
    }
  };

  const validateInputs = (
    event: any,
    v: { name: string; message: string },
    tuple: Array<any>,
    key: string
  ) => {
    const replaceCurrency = (el: string) => {
      if (el.includes(',')) return el.replace(',', '.');
      if (el.includes('R$')) {
        return parseInt(el.split('R$')[1].replaceAll('.', ''));
      } else {
        return parseInt(el);
      }
    };
    tuple[1] = {
      min: tuple[1].min.length > 0 ? replaceCurrency(tuple[1].min) : '',
      max: tuple[1].max.length > 0 ? replaceCurrency(tuple[1].max) : '',
    };
    if (Number(tuple[1].min) > Number(tuple[1].max)) {
      setValidation({ ...validation, [event.name]: v.message });
    } else if (!tuple[1].min && !tuple[1].max) {
      setValidation({
        ...validation,
        [`${key}min`]: false,
        [`${key}max`]: false,
      });
    } else {
      setValidation({
        ...validation,
        [`${key}min`]: false,
        [`${key}max`]: false,
      });
    }
  };

  const dataValidacao = (value: number) => {
    return value;
  };

  const handleInputCalendar = (e: any, v: any) => {
    const newMinDate = e.value.min.length > 0 ? e.value.min : 0;
    const newMaxDate = e.value.max.length > 0 ? e.value.max : 0;
    if (e.value.min && e.value.max) {
      if (dataValidacao(e.value.min) <= dataValidacao(e.value.max)) {
        setValidation({ ...validation, [e.name]: false });
      } else {
        setValidation({ ...validation, [e.name]: 'Final menor que inicial' });
      }
    } else {
      if (e.value.min === '' && e.value.max === '') {
        setValidation({ ...validation, [e.name]: false });
      } else {
        setValidation({ ...validation, [e.name]: 'Preencha os campos' });
      }
    }
    dispatch([
      setMinMaxDateLocalFilters({ max: newMaxDate, min: newMinDate }),
      updateInputIntervals(e),
    ]);
  };

  const handleClickSelect = (e: any, arrayName: string) => {
    const payload = {
      name: arrayName,
      value: e.value,
    };
    dispatch(updateRealtyTransactions(payload));
  };

  const handleSubmit = () => {
    const currentCityId = elastic_city_id;

    if (clusters.activeMap_ !== undefined) {
      markers.forEach((el: any) => {
        el.setMap(null);
      });
      clusters.clearMarkers();
      dispatch([updateMarkers([]), updateClusters([])]);
    }

    dispatch([
      validateForm(
        realtyTransactions,
        validation,
        closeModalAction,
        currentCityId
      ),
      updateRealtyTransactionsFiltersAdd({ name: 'filterAddT', value: false }),
      setMinUsefulArea(minMaxArea.min),
      setMaxUsefulArea(minMaxArea.max),
      setMinDate(minMaxDate.min),
      setMaxDate(minMaxDate.max),
    ]);
  };

  const handleClearFilters = () => {
    dispatch([
      setValidation({}),
      setMinMaxAreaLocalFilters({ min: 0, max: 0 }),
      setMinMaxDateLocalFilters({ min: 0, max: 0 }),
      clearFiltersTransactions(),
    ]);
  };

  return (
    <MenuWrapper
      title={nls.transactionsMarketMenuTitle}
      market="Transactions"
      submitButtonLabel={filtersErrorMessage ? nls.toSearch : nls.toApply}
      clearButtonLabel={nls.clearFilters}
      submitAction={handleSubmit}
      clearAction={handleClearFilters}
      isLoading={!avaliableInputIntervals}
      withoutCleanAction={filtersErrorMessage ? true : false}
    >
      <div className="realty-transactions">
        {avaliableInputIntervals ? (
          <>
            {realtyTypeFilterIsActive && (
              <>
                <div className="inputs-container-title">
                  <span
                    data-cy={`ContainerTitleRealtyType`}
                    className="title-span"
                  >
                    {nls.realtyType}
                  </span>
                </div>
                <Select
                  label=""
                  cy="RealtyType"
                  options={realtyTypes}
                  action={(e: any) => handleClickSelect(e, 'realtyType')}
                  selected={realtyTransactions.realtyType || ''}
                  name="realtyType"
                />
              </>
            )}

            <div className="inputs-full-container">
              {Object.entries(avaliableInputIntervals).map(
                (el: any, index: number) => (
                  <div
                    className="timeframe-container row-container"
                    key={`input-container-${index}`}
                  >
                    <div className="inputs-container-title">
                      <span
                        data-cy={`ContainerTitle${el[0]}`}
                        className="title-span"
                      >
                        {nls[el[0]]}
                      </span>
                    </div>
                    {el[0] === 'timeframe' ? (
                      <div className="calendario-ano">
                        <div className="row-container inputs-container">
                          <section className="calendar-left-selector">
                            <SelectCalendar
                              name={`${el[0]}`}
                              type="avaliableYears"
                              placeholder={nls.initial}
                              action={(e: any, v: any) => {
                                handleInputCalendar(
                                  {
                                    name: e.name,
                                    value: { ...el[1], min: e.value },
                                  },
                                  v
                                );
                              }}
                              selected={el[1].min}
                              withIcon
                              dataInicio={avaliableFilterYears[0]}
                              dataFim={avaliableFilterYears?.slice(-1)[0]}
                              yearsAvaliable={avaliableFilterYears}
                              cy="min"
                              required={
                                inputPatterns[
                                  `${el[0]}min` as keyof TransactionsGeneralInputs
                                ]
                              }
                            />
                          </section>
                          <section className="calendar-right-selector">
                            <SelectCalendar
                              name={`${el[0]}`}
                              type="avaliableYears"
                              placeholder={nls.final}
                              action={(e: any, v: any) => {
                                handleInputCalendar(
                                  {
                                    name: e.name,
                                    value: { ...el[1], max: e.value },
                                  },
                                  v
                                );
                              }}
                              selected={el[1].max}
                              withIcon
                              dataInicio={avaliableFilterYears[0]}
                              dataFim={avaliableFilterYears?.slice(-1)[0]}
                              yearsAvaliable={avaliableFilterYears}
                              cy="max"
                              required={
                                inputPatterns[
                                  `${el[0]}max` as keyof TransactionsGeneralInputs
                                ]
                              }
                            />
                          </section>
                        </div>
                        {validation?.[el[0]] ? (
                          <span
                            className="campo-obrigatorio-erro"
                            data-cy="MandatorySelectCalendaraFieldSpan"
                          >
                            {validation[el[0]]}
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <div className="row-container inputs-container">
                        <div>
                          <Input
                            cy={`${el[0]}Min`}
                            action={(e, v) => handleInput(e, v, el[1])}
                            actionBlur={(e, v) =>
                              validateInputs(
                                e,
                                { ...v, message: 'biggerThanMax' },
                                el,
                                el[0]
                              )
                            }
                            name={`${el[0]}min`}
                            placeholder={nls.initial}
                            value={el[1].min}
                            required={
                              inputPatterns[
                                `${el[0]}min` as keyof TransactionsGeneralInputs
                              ]
                            }
                            key={`${el[0]}Min`}
                          />
                        </div>
                        <div>
                          <Input
                            cy={`${el[0]}Max`}
                            action={(e, v) => handleInput(e, v, el[1])}
                            actionBlur={(e, v) =>
                              validateInputs(
                                e,
                                { ...v, message: 'smallerThanMin' },
                                el,
                                el[0]
                              )
                            }
                            name={`${el[0]}max`}
                            placeholder={nls.final}
                            value={el[1].max}
                            required={
                              inputPatterns[
                                `${el[0]}max` as keyof TransactionsGeneralInputs
                              ]
                            }
                            key={`${el[0]}Max`}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          </>
        ) : (
          <>
            {filtersErrorMessage ? (
              <div className="filters-error">
                <p className="filters-error__message">{filtersErrorMessage}</p>
              </div>
            ) : (
              <Spinner withText textValue={nls.loadingState.avaliableFilters} />
            )}
          </>
        )}
      </div>
    </MenuWrapper>
  );
};

export default RealtyTransactionsGeneral;
